import React, { useCallback, useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import qs from 'qs';
import { Helmet } from 'react-helmet';

import { Box, Container } from '@mui/material';

import PageSubheader from '~/components/Common/Elements/PageSubheader';
import ForcedRankingsConfigTab from '~/components/ForcedRankingsConfig/ForcedRankingsConfigTab';

import useAppNavigation from '~/hooks/useAppNavigation';
import useCurrentTenant from '~/hooks/useCurrentTenant';
import useQuery from '~/hooks/useQuery';

import {
  ForcedRankingsConfig as ForcedRankingsConfigType,
  deleteForcedRankingsConfig,
  listForcedRankingsConfigs,
} from '~/services/PublicOfferFeedService';

function ForcedRankingsConfigContainer() {
  const query = useQuery();
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState<Array<ForcedRankingsConfigType>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pageNum, setPageNum] = useState<number>(query.get('page_num') ? Number(query.get('page_num')) : 0);
  const [pageSize, setPageSize] = useState<number>(query.get('page_size') ? Number(query.get('page_size')) : 10);
  const [countryCode, setCountryCode] = useState<string>(query.get('country_code') || '');
  const [distanceLookup, setDistanceLookup] = useState<'10km' | '100km' | '1000km' | 'country_radius' | ''>(
    (query.get('distance_lookup') as '10km' | '100km' | '1000km' | 'country_radius') || '',
  );
  const [totalCount, setTotalCount] = useState(0);
  const { push } = useAppNavigation();
  const [editableConfig, setEditableConfig] = useState<ForcedRankingsConfigType | null>(null);
  const tenant = useCurrentTenant();

  // Modal state
  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setEditableConfig(null);
    setOpen(true);
  };

  const handleEditConfig = (config: ForcedRankingsConfigType) => {
    setEditableConfig(config);
    setOpen(true);
  };

  const setQueryParams = useCallback(() => {
    const searchParams = qs.stringify({
      page_num: pageNum.toString(),
      page_size: pageSize.toString(),
      country_code: countryCode,
      distance_lookup: distanceLookup,
    });
    push({
      search: `?${searchParams}`,
    });
  }, [push, pageNum, pageSize, countryCode, distanceLookup]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await listForcedRankingsConfigs({
        page: pageNum,
        limit: pageSize,
        country_code: countryCode || undefined,
        distance_lookup: distanceLookup || undefined,
        brand: tenant.tenant.brand,
      });

      const configs = response.result?.configs || [];
      setData(configs);
      setTotalCount(response.count);
      setQueryParams();
    } catch (error) {
      enqueueSnackbar('Forced Rankings Config fetch failed', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [pageNum, pageSize, countryCode, distanceLookup, setQueryParams, enqueueSnackbar, tenant.tenant.brand]);

  const handleDeleteConfig = useCallback(
    async (config: ForcedRankingsConfigType) => {
      try {
        await deleteForcedRankingsConfig(config.id);
        enqueueSnackbar('Deleted the forced rankings config successfully', { variant: 'success' });
        fetchData();
      } catch (error) {
        enqueueSnackbar(`Failed to delete the forced rankings config: ${error}`, { variant: 'error' });
      }
    },
    [enqueueSnackbar, fetchData],
  );

  useEffect(() => {
    const timer = setTimeout(() => fetchData(), 500);
    return () => clearTimeout(timer);
  }, [pageNum, pageSize, countryCode, distanceLookup, fetchData]);

  const onPageChange = useCallback(
    (page: number, pageSize: number) => {
      setPageNum(page);
      setPageSize(pageSize);
      query.set('page_num', page.toString());
      query.set('page_size', pageSize.toString());
      window.history.pushState({}, '', `${window.location.pathname}?${query.toString()}`);
      fetchData();
    },
    [fetchData, query],
  );

  return (
    <Container sx={{ width: '90%' }}>
      <Helmet>
        <title>Forced Rankings Config</title>
      </Helmet>

      <Box>
        <PageSubheader title={`Forced Rankings Config [${data?.length} of ${totalCount}]`} />
        <ForcedRankingsConfigTab
          countryCode={countryCode}
          setCountryCode={setCountryCode}
          distanceLookup={distanceLookup}
          setDistanceLookup={setDistanceLookup}
          data={data}
          isLoading={isLoading}
          totalCount={totalCount}
          pageNum={pageNum}
          pageSize={pageSize}
          onPageChange={onPageChange}
          handleOpen={handleOpen}
          handleEditConfig={handleEditConfig}
          handleDeleteConfig={handleDeleteConfig}
          open={open}
          setOpen={setOpen}
          editableConfig={editableConfig}
          fetchData={fetchData}
        />
      </Box>
    </Container>
  );
}

export default ForcedRankingsConfigContainer;
